import { useEffect, useRef } from "react"

const HIDE_THRESHOLD_PX = 28
const SHOW_THRESHOLD_PX = 18
const TOP_DEADZONE_PX = 80
const DIRECTION_COOLDOWN_MS = 300
const BOUNCE_GUARD_MS = 500
const HIDE_TRANSITION = "transform 250ms ease-in-out"

type Options = {
  /** When false, the hook is a no-op (no listener attached). Used to disable on desktop. */
  enabled: boolean
  /** Disable the hide transition entirely. */
  reducedMotion: boolean
  /** Called only when the hidden state flips. */
  setHeaderHidden: (hidden: boolean) => void
}

export const useAutoHideHeader = (
  headerRef: React.RefObject<HTMLElement> | { current: HTMLElement | null },
  { enabled, reducedMotion, setHeaderHidden }: Options
): void => {
  // Stable setter ref so the effect doesn't re-bind if the parent passes a new function reference.
  const setHiddenRef = useRef(setHeaderHidden)
  setHiddenRef.current = setHeaderHidden
  const reducedMotionRef = useRef(reducedMotion)
  reducedMotionRef.current = reducedMotion

  useEffect(() => {
    if (!enabled) return
    const node = headerRef.current
    if (!node) return

    let direction: "up" | "down" = "up"
    let hidden = false
    let revealBaseline = window.scrollY
    let peakWhileHidden = window.scrollY
    let lastChangeTime = 0
    let lastAtTopTime = 0
    let rafId: number | null = null
    let initialRafId: number | null = null

    const writeHidden = (next: boolean) => {
      if (hidden === next) return
      hidden = next
      // Reads first, writes after. Set transition before transform so the
      // browser sees the right transition value when the transform commits.
      const height = node.clientHeight
      node.style.transition = next && !reducedMotionRef.current ? HIDE_TRANSITION : "none"
      node.style.transform = next ? `translateY(-${height}px)` : "translateY(0)"
      if (next) node.setAttribute("data-hidden", "")
      else node.removeAttribute("data-hidden")
      setHiddenRef.current(next)
    }

    const apply = () => {
      rafId = null
      const y = window.scrollY
      const now = Date.now()

      // Arm bounce suppression anywhere in the top zone (iOS Chrome can settle
      // momentum at y>0 without ever delivering a y<=0 tick).
      if (y <= TOP_DEADZONE_PX) lastAtTopTime = now
      const isBouncing = now - lastAtTopTime < BOUNCE_GUARD_MS

      // Hard-lock to "up" inside the top zone.
      if (y < TOP_DEADZONE_PX) {
        direction = "up"
        revealBaseline = y
        lastChangeTime = now
        writeHidden(false)
        return
      }

      if (direction === "up") {
        if (y < revealBaseline) revealBaseline = y
        if (y > revealBaseline + HIDE_THRESHOLD_PX && !isBouncing && now - lastChangeTime >= DIRECTION_COOLDOWN_MS) {
          direction = "down"
          peakWhileHidden = y
          lastChangeTime = now
          writeHidden(true)
        }
      } else {
        if (y > peakWhileHidden) peakWhileHidden = y
        if (y < peakWhileHidden - SHOW_THRESHOLD_PX && now - lastChangeTime >= DIRECTION_COOLDOWN_MS) {
          direction = "up"
          revealBaseline = y
          lastChangeTime = now
          writeHidden(false)
        }
      }
    }

    const onScroll = () => {
      if (rafId !== null) return
      rafId = window.requestAnimationFrame(apply)
    }

    // iOS Chrome URL-bar resize during momentum fires visualViewport resize.
    // Treat each resize as a fresh top touch so the bounce guard stays armed.
    const onViewportResize = () => {
      lastAtTopTime = Date.now()
    }

    window.addEventListener("scroll", onScroll, { passive: true })
    const vv = window.visualViewport
    vv?.addEventListener("resize", onViewportResize)

    // Defer initial read so it doesn't land in commit phase on scroll-restored navigation.
    initialRafId = window.requestAnimationFrame(apply)

    return () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId)
      if (initialRafId !== null) window.cancelAnimationFrame(initialRafId)
      window.removeEventListener("scroll", onScroll)
      vv?.removeEventListener("resize", onViewportResize)
      // Reset inline styles + attr so a remount of the hook (e.g. enabling on resize) starts clean.
      node.style.transition = ""
      node.style.transform = ""
      node.removeAttribute("data-hidden")
    }
  }, [enabled, headerRef])
}
